import React, { useEffect, useState } from "react";
import { Card, Form, Select, InputNumber, Button, Typography } from "antd";
import { updateSiteMetadata } from "../../../utils/api.js";

const { Text } = Typography;

const MEASUREMENT_OPTIONS = [
  { name: "Rows", value: "row" },
  { name: "Total Yield (kg)", value: "total_yield" },
  { name: "Number of Pallets", value: "pallet" },
  { name: "Number of Crates", value: "crate" },
];

const LABEL_FLEX = "260px"; // label column width
const INPUT_MAX_W = 320; // max width for inputs

export default function HarvestConfig({
  messageApi,
  currentSite,
  currentCrop,
  siteMetadata,
  setSiteMetadata,
}) {
  const [saving, setSaving] = useState(false);

  const [unit, setUnit] = useState("row");
  const [yieldPerM, setYieldPerM] = useState(0);
  const [cratesPerPallet, setCratesPerPallet] = useState(0);
  const [weightPerCrate, setWeightPerCrate] = useState(0);

  useEffect(() => {
    const cfg = siteMetadata?.HARVEST_OPTIMIZER || {};

    if (cfg.DEFAULT_UNIT_OF_MEASUREMENT)
      setUnit(cfg.DEFAULT_UNIT_OF_MEASUREMENT);
    if (Number.isFinite(Number(cfg.YIELD_PER_METRE_KG_PER_M)))
      setYieldPerM(Number(cfg.YIELD_PER_METRE_KG_PER_M));
    if (Number.isFinite(Number(cfg.CRATES_PER_PALLET)))
      setCratesPerPallet(Number(cfg.CRATES_PER_PALLET));
    if (Number.isFinite(Number(cfg.WEIGHT_PER_CRATE_KG)))
      setWeightPerCrate(Number(cfg.WEIGHT_PER_CRATE_KG));
  }, [siteMetadata]);

  const onSave = async () => {
    if (!currentSite?.id) return messageApi.error("Site not selected.");
    if (!currentCrop?.name) return messageApi.error("Crop not selected.");

    // basic validation (no NaN / negatives)
    const nums = [
      ["Yield per metre (kg/m)", yieldPerM],
      ["Crates per pallet", cratesPerPallet],
      ["Weight per crate (kg)", weightPerCrate],
    ];
    for (const [label, v] of nums) {
      if (!Number.isFinite(Number(v)) || Number(v) < 0) {
        messageApi.error(`${label} must be a non-negative number.`);
        return;
      }
    }

    setSaving(true);
    try {
      const updatedMetadata = { ...(currentSite?.metadata || {}) };
      if (!updatedMetadata[currentCrop.name]) {
        updatedMetadata[currentCrop.name] = {};
      }
      updatedMetadata[currentCrop.name].HARVEST_OPTIMIZER = {
        ...(siteMetadata.HARVEST_OPTIMIZER || {}),
        DEFAULT_UNIT_OF_MEASUREMENT: unit,
        YIELD_PER_METRE_KG_PER_M: Number(yieldPerM),
        CRATES_PER_PALLET: Number(cratesPerPallet),
        WEIGHT_PER_CRATE_KG: Number(weightPerCrate),
      };

      const res = await updateSiteMetadata(currentSite.id, {
        metadata: updatedMetadata,
      });

      if (res?.status === 200) {
        setSiteMetadata({ ...updatedMetadata[currentCrop.name] });
        messageApi.success("Configuration saved.");
      } else {
        messageApi.error("Failed to save configuration.");
      }
    } catch (e) {
      messageApi.error(e?.message || "Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <div className="flex items-center justify-between mb-4">
        <Text strong>Harvest Optimizer — Configuration</Text>
        <Button type="primary" onClick={onSave} loading={saving}>
          Save
        </Button>
      </div>

      <Form
        layout="horizontal"
        labelAlign="left"
        colon={false}
        labelCol={{ flex: LABEL_FLEX }}
        wrapperCol={{ flex: "auto" }}
        className="space-y-0"
      >
        <Form.Item label="Default Unit of Measurement" className="!mb-3">
          <Select
            value={unit}
            onChange={setUnit}
            options={MEASUREMENT_OPTIONS.map((o) => ({
              label: o.name,
              value: o.value,
            }))}
            popupMatchSelectWidth={false}
            className="w-full"
            style={{ maxWidth: INPUT_MAX_W }}
          />
        </Form.Item>

        <Form.Item label="Yield per metre (kg/m)" className="!mb-3">
          <InputNumber
            min={0}
            value={yieldPerM}
            onChange={(v) => setYieldPerM(Number(v) || 0)}
            className="w-full"
            style={{ maxWidth: INPUT_MAX_W }}
          />
        </Form.Item>

        <Form.Item label="Crates per Pallet" className="!mb-3">
          <InputNumber
            min={0}
            precision={0}
            value={cratesPerPallet}
            onChange={(v) => setCratesPerPallet(Number(v) || 0)}
            className="w-full"
            style={{ maxWidth: INPUT_MAX_W }}
          />
        </Form.Item>

        <Form.Item label="Weight per Crate (kg)" className="!mb-0">
          <InputNumber
            min={0}
            value={weightPerCrate}
            onChange={(v) => setWeightPerCrate(Number(v) || 0)}
            className="w-full"
            style={{ maxWidth: INPUT_MAX_W }}
          />
        </Form.Item>
      </Form>
    </Card>
  );
}
